// DOM helpers (§27.1): no framework. `el()` builds elements, `mount()` attaches them, `spring()`
// plays a damped spring on the element's `scale` with the Web Animations API. Per-frame writers
// (`setText`, `setHidden`, `toggleClass`, `setStyle`) only touch the DOM when the value changed.

type Child = Node | string | number | null | undefined | false;
type Listeners = { [K in keyof HTMLElementEventMap]?: (e: HTMLElementEventMap[K]) => void };

export interface ElProps {
  class?: string;
  text?: string;
  attrs?: Record<string, string | number | boolean | undefined>;
  on?: Listeners;
  style?: Record<string, string>;
}

/** Creates `<tag>` with classes, attributes (false/undefined skipped, true → ""), listeners and children. */
export function el<K extends keyof HTMLElementTagNameMap>(tag: K, props?: ElProps | null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props) {
    if (props.class) node.className = props.class;
    if (props.text !== undefined) node.textContent = props.text;
    if (props.attrs) {
      for (const [k, v] of Object.entries(props.attrs)) {
        if (v === false || v === undefined) continue;
        node.setAttribute(k, v === true ? '' : String(v));
      }
    }
    if (props.style) for (const [k, v] of Object.entries(props.style)) node.style.setProperty(k, v);
    if (props.on) {
      for (const [type, fn] of Object.entries(props.on)) node.addEventListener(type, fn as EventListener);
    }
  }
  append(node, children);
  return node;
}

function append(node: ParentNode, children: readonly Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    node.append(typeof c === 'number' ? String(c) : c);
  }
}

export const SVG_NS = 'http://www.w3.org/2000/svg';

/** Creates an SVG element with attributes and children. */
export function svg<K extends keyof SVGElementTagNameMap>(tag: K, attrs?: Record<string, string | number> | null, ...children: (SVGElement | null | false)[]): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  if (attrs) for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  for (const c of children) if (c) node.append(c);
  return node;
}

export function mount<T extends Node>(parent: ParentNode, node: T): T {
  parent.append(node);
  return node;
}

/** Writes `text` only when it changed (per-frame HUD updates are diffed, §27.2). */
export function setText(node: Element, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}

export function setHidden(node: HTMLElement | SVGElement, hidden: boolean): void {
  if (node instanceof HTMLElement) {
    if (node.hidden !== hidden) node.hidden = hidden;
  } else if (node.hasAttribute('visibility') !== hidden) {
    if (hidden) node.setAttribute('visibility', 'hidden');
    else node.removeAttribute('visibility');
  }
}

export function toggleClass(node: Element, cls: string, on: boolean): void {
  if (node.classList.contains(cls) !== on) node.classList.toggle(cls, on);
}

export function setStyle(node: HTMLElement | SVGElement, prop: string, value: string): void {
  if (node.style.getPropertyValue(prop) !== value) node.style.setProperty(prop, value);
}

export function setAttr(node: Element, name: string, value: string): void {
  if (node.getAttribute(name) !== value) node.setAttribute(name, value);
}

/** Restarts a CSS animation class on `node` (removing and re-adding it after a reflow). */
export function replayClass(node: HTMLElement, cls: string): void {
  node.classList.remove(cls);
  void node.offsetWidth;
  node.classList.add(cls);
}

/** True when decorative motion must be skipped (Reduced Motion setting or the OS preference). */
export function reducedMotion(): boolean {
  return document.getElementById('ui')?.classList.contains('reduced-motion') === true
    || (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches);
}

/** Damped spring keyframes from `from` to `to` (stiffness/damping per §11.5). */
export function springFrames(from: number, to: number, stiffness = 300, damping = 20, frames = 40, durationMs = 560): number[] {
  const out: number[] = [];
  let x = from;
  let v = 0;
  const dt = durationMs / 1000 / frames;
  for (let i = 0; i <= frames; i++) {
    out.push(x);
    // Semi-implicit integration in a few sub-steps keeps stiff springs stable.
    for (let k = 0; k < 4; k++) {
      v += (stiffness * (to - x) - damping * v) * (dt / 4);
      x += v * (dt / 4);
    }
  }
  out[out.length - 1] = to;
  return out;
}

/** Springs the element's scale from `from` to `to` (panel transitions, stamps). */
export function spring(node: HTMLElement, from: number, to: number, stiffness = 300, damping = 20): Animation | null {
  if (typeof node.animate !== 'function' || reducedMotion()) return null;
  const values = springFrames(from, to, stiffness, damping);
  const frames: Keyframe[] = values.map((s, i) => ({ scale: String(s), opacity: i === 0 ? 0 : Math.min(1, i / 8) }));
  return node.animate(frames, { duration: 560, easing: 'linear' });
}

/** Copies text to the clipboard; resolves false when the browser refuses. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for insecure contexts: a hidden textarea and execCommand.
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.append(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

/** Clamps v to [lo, hi]. */
export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Reference resolution for `--vp` (§14.3 landscape 16:9 reference). */
const REF_W = 1280;
const REF_H = 720;
const VP_MIN = 0.8;
const VP_MAX = 2;

/** The viewport factor: 1 at 1280×720, 2 at 2560×1440, never below 0.8 (phones). */
export function viewportFactor(w: number, h: number): number {
  return clamp(Math.min(w / REF_W, h / REF_H), VP_MIN, VP_MAX);
}
